// src/pages/PurchaseOrderList.js
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import FilterModal from "./FilterModal";

import {
  FaSearch,
  FaFilter,
  FaPlus,
  FaMapMarkerAlt,
  FaEllipsisV,
  FaCheck,
  FaTimes,
  FaRegFileAlt,
} from "react-icons/fa";

const PurchaseOrderList = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  // Semua data PO dari backend (full dataset)
  const [allOrders, setAllOrders] = useState([]);

  // Data yang ditampilkan (setelah search, filter, pagination)
  const [displayOrders, setDisplayOrders] = useState([]);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const [globalFilter, setGlobalFilter] = useState("");

  const [selectedStatus, setSelectedStatus] = useState("All");

  // FILTER FROM FILTERMODAL
  const [filterOptions, setFilterOptions] = useState({
    distributor_id: "",
    outlet_id: "",
    status: "",
    method: "",
  });

  // Pagination frontend
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // =============================================================
  // =============== FETCH ALL DATA (LOOP SEMUA PAGE) ============
  // =============================================================
  const fetchAllOrders = async () => {
    setIsLoading(true);

    try {
      const token = localStorage.getItem("token");

      // Fetch page 1 terlebih dahulu
      const firstRes = await fetch(
        `${BASE_URL}/v1/purchasing-order?page=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const firstData = await firstRes.json();

      if (!firstData.success) return;

      const totalPages = firstData.meta.last_page;
      let completeData = [...firstData.data];

      // Loop page 2,3,...dst
      for (let page = 2; page <= totalPages; page++) {
        const res = await fetch(
          `${BASE_URL}/v1/purchasing-order?page=${page}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = await res.json();
        completeData = [...completeData, ...data.data];
      }

      // Format semua PO (harus menambah distributor_id & outlet_id)
      const formatted = completeData.map((order) => ({
        raw: order, // simpan raw kalau butuh
        distributor_id: order.distributor?.id ?? null,
        outlet_id: order.outlet?.id ?? null,

        id: encodeURIComponent(order?.po_number ?? "Owner"),
        supplier: order?.distributor?.name
          ? `${order.distributor.name} - ${order.distributor.kota ?? "N/A"}`
          : "N/A",
        outlet: order?.outlet?.name
          ? `${order.outlet.name} - ${order.outlet.kota ?? "N/A"}`
          : "N/A",
        amount: order?.total_amount
          ? `Rp ${order.total_amount.toLocaleString("id-ID", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : "Rp 0,00",
        status: order?.status
          ? order.status.charAt(0).toUpperCase() + order.status.slice(1)
          : "Owner",
        user: order?.created_by?.fullname || "Owner",
        paymentType: order?.method || "N/A",
        createdDate: new Date(order?.created_at).toLocaleDateString("id-ID"),
      }));

      setAllOrders(formatted);

    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // =============================================================
  // ============== FILTER + SEARCH + PAGINATION =================
  // =============================================================
  useEffect(() => {
    let filtered = [...allOrders];

    // TAB STATUS (All, Pending, Approved, Received, Canceled)
    if (selectedStatus !== "All") {
      filtered = filtered.filter(
        (o) => o.status.toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    // ===========================
    // FILTER FROM FILTER MODAL
    // ===========================

    // Distributor
    if (filterOptions.distributor_id) {
      filtered = filtered.filter(
        (o) => o.distributor_id === Number(filterOptions.distributor_id)
      );
    }

    // Outlet
    if (filterOptions.outlet_id) {
      filtered = filtered.filter(
        (o) => o.outlet_id === Number(filterOptions.outlet_id)
      );
    }

    // Method (cash / credit)
    if (filterOptions.method) {
      filtered = filtered.filter(
        (o) =>
          o.paymentType.toLowerCase() === filterOptions.method.toLowerCase()
      );
    }

    // STATUS from modal
    if (filterOptions.status) {
      filtered = filtered.filter(
        (o) =>
          o.status.toLowerCase() === filterOptions.status.toLowerCase()
      );
    }

    // ===========================
    // SEARCH
    // ===========================
    const s = globalFilter.toLowerCase();
    if (s.trim() !== "") {
      filtered = filtered.filter((o) => {
        return (
          decodeURIComponent(o.id).toLowerCase().includes(s) ||
          o.supplier.toLowerCase().includes(s) ||
          o.outlet.toLowerCase().includes(s) ||
          o.amount.toLowerCase().includes(s) ||
          o.user.toLowerCase().includes(s) ||
          o.status.toLowerCase().includes(s)
        );
      });
    }

    // Hitung total halaman
    const pages = Math.ceil(filtered.length / pageSize);
    setTotalPages(pages || 1);

    // Pagination slicing
    const start = (pageIndex - 1) * pageSize;
    const end = start + pageSize;

    setDisplayOrders(filtered.slice(start, end));
  }, [
    allOrders,
    globalFilter,
    selectedStatus,
    pageIndex,
    pageSize,
    filterOptions,
  ]);

  // Reset ke page 1 saat search atau status berubah
  useEffect(() => {
    setPageIndex(1);
  }, [globalFilter, selectedStatus, filterOptions]);

  // Fetch semua data saat page load
  useEffect(() => {
    fetchAllOrders();
  }, []);

  // =============================================================
  // ====================== APPROVE PO ============================
  // =============================================================
  const handleApprove = async (id) => {
    const token = localStorage.getItem("token");

    try {
      await fetch(`${BASE_URL}/v1/purchasing-order/${id}/approve`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });

      Swal.fire("Berhasil!", "PO berhasil di-approve!", "success");
      fetchAllOrders();
    } catch {
      Swal.fire("Error", "Terjadi kesalahan saat approve.", "error");
    }
  };

  // =============================================================
  // ====================== CANCEL PO =============================
  // =============================================================
  const handleCancel = async (id) => {
    const token = localStorage.getItem("token");

    const confirm = await Swal.fire({
      title: "Batalkan PO?",
      text: "Status PO akan kembali Pending.",
      icon: "warning",
      showCancelButton: true,
    });

    if (!confirm.isConfirmed) return;

    try {
      await fetch(`${BASE_URL}/v1/purchasing-order/${id}/delete`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      Swal.fire("Dibatalkan!", "PO berhasil dibatalkan.", "success");
      fetchAllOrders();
    } catch {
      Swal.fire("Error", "Gagal membatalkan PO.", "error");
    }
  };

  // Pagination control
  const nextPage = () => {
    if (pageIndex < totalPages) setPageIndex((p) => p + 1);
  };
  const prevPage = () => {
    if (pageIndex > 1) setPageIndex((p) => p - 1);
  };

  // =============================================================
  // ============================ UI ==============================
  // =============================================================
  return (
    <div className="p-6 bg-gradient-to-b from-white to-green-50 min-h-screen rounded-2xl">

      {/* Breadcrumb */}
      <nav className="text-gray-500 text-sm mb-3">
        <ol className="flex items-center gap-2">
          <li>Inventory</li>
          <li>/</li>
          <li className="font-medium text-gray-700">Purchasing Order</li>
        </ol>
      </nav>

      {/* Title */}
      <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2 mb-4">
        <div className="p-3 bg-green-100 rounded-2xl shadow-inner">
          <FaRegFileAlt className="text-green-600" />
        </div>
        Purchasing Order
      </h2>

      <p className="text-gray-600 mb-4">Kelola dan pantau semua PO dengan mudah</p>

      {/* Search */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative w-full md:w-1/3">
          <input
            type="text"
            placeholder="Cari purchase order..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="w-full px-5 py-3 rounded-2xl border shadow-sm"
          />
          <FaSearch className="absolute right-4 top-3.5 text-gray-400" />
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className="px-5 py-2.5 bg-white border rounded-2xl shadow-sm"
          >
            <FaFilter className="text-green-700 inline-block mr-2" />
            Filter
          </button>

          <button
            onClick={() =>
              navigate("/inventory/purchase-order/purchasingorderformadmin")
            }
            className="px-5 py-2.5 bg-green-600 text-white rounded-2xl shadow-md"
          >
            <FaPlus className="inline-block mr-2" />
            Create PO
          </button>
        </div>
      </div>

      {/* STATUS TABS */}
      <div className="flex gap-2 flex-wrap mb-6">
        {["All", "Pending", "Approved", "Received", "Canceled"].map((status) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(status)}
            className={`px-4 py-2 rounded-2xl text-sm ${
              selectedStatus === status
                ? "bg-green-200 text-green-800"
                : "bg-white border"
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* LIST */}
      <div className="space-y-6">
        {isLoading ? (
          <p className="text-gray-500">Loading orders...</p>
        ) : (
          displayOrders.map((order, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-3xl shadow-lg border hover:shadow-xl"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    {decodeURIComponent(order.id)} • {order.supplier}
                  </h3>

                  <div className="flex items-center text-sm text-gray-600 gap-2 mt-2">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100 text-green-700 font-bold shadow">
                      {order.user.charAt(0)}
                    </span>
                    <span>{order.user}</span>
                    <FaMapMarkerAlt className="text-gray-400" />
                    <span>{order.outlet}</span>

                    <span className="ml-2 text-green-700 font-semibold">
                      {order.paymentType}
                    </span>
                  </div>
                </div>

                {/* Dropdown */}
                <div className="relative">
                  <FaEllipsisV
                    className="text-xl cursor-pointer text-gray-600"
                    onClick={() =>
                      setOpenDropdown(openDropdown === index ? null : index)
                    }
                  />

                  {openDropdown === index && (
                    <div className="absolute right-0 mt-3 w-52 bg-white rounded-2xl border shadow-xl">
                      <a
                        href={`/inventory/purchase-order/detail/${order.id}`}
                        className="block px-4 py-3 hover:bg-gray-100 text-sm"
                      >
                        <FaSearch className="inline-block mr-2" />
                        View Detail
                      </a>

                      {order.status === "Pending" && (
                        <>
                          <button
                            onClick={() => handleApprove(order.id)}
                            className="block w-full text-left px-4 py-3 hover:bg-gray-100 text-sm"
                          >
                            <FaCheck className="inline-block mr-2 text-green-600" />
                            Approve
                          </button>

                          <button
                            onClick={() => handleCancel(order.id)}
                            className="block w-full text-left px-4 py-3 hover:bg-red-50 text-sm text-red-600"
                          >
                            <FaTimes className="inline-block mr-2" />
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 bg-gray-50 px-4 py-2 rounded-xl text-sm">
                <strong>Created:</strong> {order.createdDate}
              </div>

              <div className="mt-4 flex justify-between items-center">
                <p className="text-2xl font-bold text-gray-800">{order.amount}</p>

                <span
                  className={`text-xs px-4 py-1.5 rounded-full shadow font-medium ${
                    order.status === "Pending"
                      ? "bg-yellow-100 text-yellow-700"
                      : order.status === "Approved"
                      ? "bg-green-100 text-green-700"
                      : order.status === "Canceled"
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {order.status}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      <div className="mt-10 flex justify-between items-center text-sm">
        <button
          onClick={prevPage}
          disabled={pageIndex === 1}
          className="px-5 py-2 border rounded-full bg-white shadow disabled:opacity-40"
        >
          Previous
        </button>

        <span>
          Page <strong>{pageIndex}</strong> of{" "}
          <strong>{totalPages}</strong> • Total:{" "}
          <strong>{allOrders.length}</strong>
        </span>

        <button
          onClick={nextPage}
          disabled={pageIndex >= totalPages}
          className="px-5 py-2 border rounded-full bg-white shadow disabled:opacity-40"
        >
          Next
        </button>
      </div>

      {/* Page size */}
      <div className="mt-4 flex items-center gap-2 text-sm">
        <span>Show</span>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="px-3 py-2 border rounded-xl bg-white"
        >
          {[10, 25, 50].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <span>items per page</span>
      </div>

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={(f) => {
          setFilterOptions(f);
          setPageIndex(1);
        }}
      />
    </div>
  );
};

export default PurchaseOrderList;
